import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { CoverPage } from '../../models';

@Component({
  selector: 'app-cover-page-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatSnackBarModule
  ],
  template: `
    <h2 mat-dialog-title>{{ data.coverPage ? 'Editar Portada' : 'Nueva Portada' }}</h2>

    <mat-dialog-content>
      <form [formGroup]="form" class="cover-page-form">
        <div class="form-row">
          <mat-form-field appearance="outline" class="form-field">
            <mat-label>Año</mat-label>
            <mat-select formControlName="year">
              <mat-option *ngFor="let year of availableYears" [value]="year">
                {{ year }}
              </mat-option>
            </mat-select>
            <mat-error *ngIf="form.get('year')?.hasError('required')">
              El año es requerido
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="form-field">
            <mat-label>Mes</mat-label>
            <mat-select formControlName="month">
              <mat-option *ngFor="let month of months" [value]="month.value">
                {{ month.name }}
              </mat-option>
            </mat-select>
            <mat-error *ngIf="form.get('month')?.hasError('required')">
              El mes es requerido
            </mat-error>
          </mat-form-field>
        </div>

        <div 
          class="upload-zone"
          [class.drag-over]="isDragOver"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event)"
          (click)="fileInput.click()"
        >
          <div class="upload-content">
            <mat-icon class="upload-icon">cloud_upload</mat-icon>
            <p class="upload-text">
              <span class="highlight">Arrastra y suelta</span> una imagen aquí
            </p>
            <p class="upload-subtext">o haz clic para seleccionar</p>
            <p class="file-info">
              JPG, PNG, GIF, BMP, WEBP • Máximo 15MB
            </p>
          </div>
          <input 
            #fileInput 
            type="file" 
            (change)="onFileSelected($event)" 
            accept="image/*" 
            style="display: none;" 
          />
        </div>

        <div *ngIf="selectedFile || data.coverPage" class="preview-section">
          <div class="preview-header">
            <mat-icon>image</mat-icon>
            <span>Vista previa</span>
          </div>
          <div class="preview-container">
            <img *ngIf="selectedFile" [src]="previewUrl" alt="Vista previa" />
            <img *ngIf="!selectedFile && data.coverPage" [src]="data.coverPage.fileUrl" alt="Vista previa" />
          </div>
          <p class="file-name">
            {{ selectedFile?.name || data.coverPage?.fileName }}
          </p>
          <button 
            mat-stroked-button 
            color="warn" 
            class="remove-btn"
            (click)="removeFile($event)"
          >
            <mat-icon>delete</mat-icon>
            Eliminar imagen
          </button>
        </div>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancelar</button>
      <button mat-raised-button color="primary" (click)="onSave()" [disabled]="form.invalid || !selectedFile && !data.coverPage">
        {{ data.coverPage ? 'Guardar Cambios' : 'Crear Portada' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    :host {
      display: block;
    }

    h2[mat-dialog-title] {
      font-family: var(--font-display);
      font-weight: 700;
      letter-spacing: -0.02em;
    }

    .cover-page-form {
      display: flex;
      flex-direction: column;
      gap: 20px;
      padding-top: 8px;
      min-width: 400px;
    }

    .form-row {
      display: flex;
      gap: 12px;
    }

    .form-field {
      flex: 1;
    }

    mat-form-field {
      ::ng-deep .mdc-text-field--outlined {
        border-radius: 10px;
      }
    }

    .upload-zone {
      border: 2px dashed rgba(0, 0, 0, 0.15);
      border-radius: var(--radius-md);
      padding: 32px 20px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      background: #fafafa;
    }

    .upload-zone:hover {
      border-color: var(--primary-color);
      background: #fdf7f7;
    }

    .upload-zone.drag-over {
      border-color: var(--primary-color);
      background: #fef2f2;
      transform: scale(1.02);
    }

    .upload-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }

    .upload-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
      color: #757575;
      transition: color 0.3s ease;
    }

    .upload-zone:hover .upload-icon {
      color: var(--primary-color);
    }

    .upload-text {
      margin: 0;
      font-size: 16px;
      color: var(--text-primary);
      font-weight: 500;
    }

    .highlight {
      color: var(--primary-color);
      font-weight: 600;
    }

    .upload-subtext {
      margin: 0;
      font-size: 14px;
      color: var(--text-secondary);
    }

    .file-info {
      margin: 8px 0 0 0;
      font-size: 12px;
      color: #9e9e9e;
    }

    .preview-section {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 16px;
      background: #fafafa;
      border-radius: var(--radius-md);
    }

    .preview-header {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 500;
      color: var(--text-primary);
    }

    .preview-container {
      width: 100%;
      height: 280px;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--radius-sm);
      overflow: hidden;
      border: 1px solid var(--border-color);

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .file-name {
      margin: 0;
      font-size: 13px;
      color: var(--text-secondary);
      word-break: break-all;
    }

    .remove-btn {
      align-self: flex-start;
      font-size: 13px;
    }

    mat-dialog-content {
      max-height: 70vh;
    }

    mat-dialog-actions {
      padding: 16px 0 0 0;
    }

    @media (max-width: 600px) {
      .cover-page-form {
        min-width: 300px;
      }

      .form-row {
        flex-direction: column;
        gap: 16px;
      }

      .upload-zone {
        padding: 24px 16px;
      }

      .preview-container {
        height: 200px;
      }
    }
  `]
})
export class CoverPageDialogComponent {
  form: FormGroup;
  selectedFile: File | null = null;
  previewUrl: string | null = null;
  availableYears: number[] = [];
  months: { name: string; value: number }[] = [];
  isDragOver = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CoverPageDialogComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: { coverPage?: CoverPage }
  ) {
    this.availableYears = this.generateAvailableYears();
    this.months = this.generateMonths();

    this.form = this.fb.group({
      year: [data.coverPage?.year || new Date().getFullYear(), Validators.required],
      month: [data.coverPage?.month || new Date().getMonth() + 1, Validators.required]
    });
  }

  generateAvailableYears(): number[] {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let i = currentYear - 2; i <= currentYear + 1; i++) {
      years.push(i);
    }
    return years;
  }

  generateMonths(): { name: string; value: number }[] {
    return [
      { name: 'Enero', value: 1 },
      { name: 'Febrero', value: 2 },
      { name: 'Marzo', value: 3 },
      { name: 'Abril', value: 4 },
      { name: 'Mayo', value: 5 },
      { name: 'Junio', value: 6 },
      { name: 'Julio', value: 7 },
      { name: 'Agosto', value: 8 },
      { name: 'Septiembre', value: 9 },
      { name: 'Octubre', value: 10 },
      { name: 'Noviembre', value: 11 },
      { name: 'Diciembre', value: 12 }
    ];
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      this.handleFile(file);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.handleFile(file);
    }
  }

  handleFile(file: File) {
    if (!this.isImageFile(file)) {
      this.snackBar.open('Por favor selecciona un archivo de imagen válido', 'Cerrar', { duration: 4000 });
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      this.snackBar.open('El archivo excede el tamaño máximo de 15MB', 'Cerrar', { duration: 4000 });
      return;
    }

    this.selectedFile = file;
    this.createPreview(file);
  }

  removeFile(event: Event) {
    event.stopPropagation();
    this.selectedFile = null;
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }

  isImageFile(file: File): boolean {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
    return allowedTypes.includes(file.type);
  }

  createPreview(file: File) {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.previewUrl = URL.createObjectURL(file);
  }

  onCancel() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.dialogRef.close();
  }

  onSave() {
    if (this.form.valid) {
      const { year, month } = this.form.value;

      if (!this.selectedFile && !this.data.coverPage) {
        this.snackBar.open('Por favor selecciona un archivo de imagen', 'Cerrar', { duration: 4000 });
        return;
      }

      this.dialogRef.close({
        year,
        month,
        file: this.selectedFile
      });
    }
  }
}
